import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { noop } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { MapMouseEvent, Map } from 'mapbox-gl';
import { Project, ProjectPagination, Pipeline, DealMapFilter } from 'app/modules/projects/projects.types';
import { Account, FieldDef } from 'app/core/models/account.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ProjectAddComponent } from 'app/modules/projects/modals/project-add/project-add.component';
import { environment } from '@env/environment';
import UrlUtils from 'app/shared/Utils/url.utils';
import { interpolateTurbo, schemeCategory10 } from 'd3-scale-chromatic';
import { Dictionary } from 'app/shared/models/dictionary';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { PipelineService } from '../../pipeline.service';
import { LayoutService } from 'app/layout/layout.service';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { getAllowedFields } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { ProjectsListService } from '../list.service';
import { AccountService, AuthService } from 'app/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { setupAddDealByDrawer } from '../../@shared/functions.utils';
import { Permission } from 'app/core/enums';

type MapFilter = {
  location: {
    top_left: { lat: number; lon: number };
    bottom_right: { lat: number; lon: number };
  };
};

type MapMouseEventExtended = MapMouseEvent & { features: any[] };

@Component({
  selector: 'projects-list-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsListMapComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  pipeline: Pipeline;
  Permission = Permission;
  projects: Project[];
  pagination: ProjectPagination;
  projectsCount: number;
  isLoading: boolean;
  searchInputControl: UntypedFormControl;
  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  displayColumnsEditMode: boolean;
  sortFields: FieldDef[] = [];
  data: any;
  isSpeadDialFixed = false;
  selectedSpeadDial: number;

  selectedSortField = 'created_at';
  groupBy = 'project_stage_id';
  groupByOptions = [];
  availableSelectFields = [];
  groupByLabel = '';
  excludedOptions = [];

  //RRS
  fieldDefs: FieldDef[] = [];
  filter: any = {};
  savedFilter: any = null;

  //map features
  map: Map;
  geoJsonUrl = '';
  geoData: any;
  optionFilteredGeoJsonData: any;
  selectedPoint: GeoJSON.Feature<GeoJSON.Point> | null;
  selectedAnalyticsPoint: GeoJSON.Feature<GeoJSON.Point> | null;
  cursorStyle: string;
  mapToggleVal = 'deals';
  circleRadiusVar = 'avg_bid_price';
  dealMapFilters: DealMapFilter[] = [
    { id: 2, iconName: 'attach_money', label: 'Bid Price', key: 'normalized_bid_price' },
    { id: 3, iconName: 'attach_money', label: 'Bid Price/Unit', key: 'normalized_bid_price_per_sq_ft' },
    { id: 4, iconName: 'attach_money', label: 'Bid PSF', key: 'normalized_bid_price_per_unit' },
    { id: 5, iconName: 'attach_money', label: 'Sales Price', key: 'normalized_sale_price' },
    { id: 6, iconName: 'attach_money', label: 'Sale Price/Unit', key: 'normalized_sale_price_per_sq_ft' },
    { id: 7, iconName: 'attach_money', label: 'Sale PSF', key: 'normalized_sale_price_per_unit' },
  ];
  account: Account;

  private filterStorageKey = 'Project_queryFilters';

  get options(): Dictionary<any> {
    return this.data.options;
  }

  get filteredGeoJsonDataFeatures(): any[] {
    return (
      this.geoData.features?.filter(({ properties: { project_id } }) => this.projectsIds.includes(project_id)) || []
    );
  }

  private get projectsIds(): number[] {
    return this.projects?.map(({ id }) => Number(id)) || [];
  }

  private get projectTemplates(): { id: number; name: string }[] {
    return this.options.project_templates
      .filter(({ hidden, related_to_type }) => !hidden && related_to_type === RelatedToType.Project)
      .sort((a, b) => a.rank - b.rank);
  }

  private get baseApiUrl(): string {
    return `${environment.baseUrl}/${environment.apiVersion}`;
  }

  private get mapFilter(): MapFilter {
    const bbox = this.map.getBounds();
    const [nw, se] = [bbox.getNorthWest(), bbox.getSouthEast()];
    return {
      location: {
        top_left: { lat: nw.lat, lon: nw.lng },
        bottom_right: { lat: se.lat, lon: se.lng },
      },
    };
  }

  private mapTouched = false;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _projectsService: ProjectsService,
    private readonly _accountService: AccountService,
    private _router: Router,
    private _matDialog: MatDialog,
    private readonly layoutService: LayoutService,
    private readonly pipelineService: PipelineService,
    private readonly _foldersTabService: FoldersTabService,
    private readonly projectsListService: ProjectsListService,
    private readonly authService: AuthService,
  ) {
    super();
    this.searchInputControl = new UntypedFormControl();
    this.pagination = {
      length: 1,
      size: 20,
      page: 0,
      lastPage: 1,
      startIndex: 0,
      endIndex: 1 - 1,
    };

    this.displayColumns = this.fieldDefs.map((fieldDef) => fieldDef.name);
  }

  ngOnInit(): void {
    UrlUtils.saveToLocalStorage('projectView', 'map');

    this.filter = UrlUtils.getFromLocalStorage(this.filterStorageKey, true);

    this._activatedRoute.data
      .pipe(
        untilComponentDestroyed(this),
        tap((data: Data) => {
          this.data = data;
          this.fieldDefs = getAllowedFields(data.fieldDefs, RelatedToType.Project);
          const fields = this.fieldDefs.filter((fieldDef) => fieldDef.name === this.groupBy);
          this.groupByLabel = fields[0].label;
          this.groupByOptions = this.data.options[fields[0].meta.option_name];
          this.availableSelectFields = this.fieldDefs.filter((obj) => obj.type === 'select');
        }),
        switchMap(() => this._accountService.currentAccount),
        tap((account: Account) => (this.account = account)),
        switchMap(() => this._projectsService.pipeline$),
        switchMap((pipeline: Pipeline) => {
          this.pipeline = pipeline;
          UrlUtils.saveToLocalStorage('projectPipeline', this.pipeline.id);
          const definition = this.pipeline.table_definition;
          if (definition) {
            this.displayColumns = definition.fields;
            for (const column of this.displayColumns) {
              let fieldDef = this.lookupField(column);
              if (fieldDef?.meta && fieldDef.meta?.sorting) {
                this.sortFields.push(fieldDef);
              }
            }
            const fieldDef = this.lookupField('created_at');
            this.sortFields.push(fieldDef);
          }

          this.geoJsonUrl = `${this.baseApiUrl}/sharing/pipelines/${this.pipeline.id}/geojson`;

          if (this.mapToggleVal == 'analytics') {
            this.geoJsonUrl += '&group_by=market';
          }

          return this._projectsService.getGeoJSON(this.geoJsonUrl);
        }),
        tap((geoData: any) => (this.geoData = geoData)),
        switchMap(() => this._activatedRoute.queryParams),
        tap((params: any) => {
          if (!Object.keys(params).length) {
            this.loadProjects();
          }
        }),
      )
      .subscribe(noop);

    this._activatedRoute.queryParams
      .pipe(
        untilComponentDestroyed(this),
        filter((params: any) => params['filterId']),
        tap(({ filterId }: { filterId: number | string }) => {
          if (Number(filterId) > 0) {
            const savedFilter = this._accountService.getSavedFiltersValue().find(({ id }) => id === Number(filterId));
            if (savedFilter) {
              this.savedFilter = savedFilter;
              this.filter = JSON.parse(savedFilter.filters);
              UrlUtils.saveToLocalStorage(this.filterStorageKey, savedFilter.filters, false);
              this.loadProjects();
            }
          } else if (['clear', 'reset'].includes(String(filterId))) {
            this.onClearFilters();
            this.loadProjects();
          }
        }),
      )
      .subscribe(noop);
  }

  loadProjects(page = 0): void {
    this.isLoading = true;

    if (this.mapTouched) {
      this.filter = { ...this.filter, ...this.mapFilter };
    }

    const query = this.filter.q || this.searchInputControl.value;

    this._projectsService
      .getProjects(this.displayColumns, this.filter, page, this.pagination.size, this.selectedSortField, 'desc', query)
      .pipe(
        debounceTime(300),
        untilComponentDestroyed(this),
        tap(({ projects, pagination }: { projects: Project[]; pagination: ProjectPagination }) => {
          this.projects = projects;
          this.pagination = pagination;
          this.isLoading = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  filterProjects(event: any): void {
    this.filter = event;
    UrlUtils.saveToLocalStorage(this.filterStorageKey, event, true);
    this.loadProjects();
  }

  onClearFilters(): void {
    this.filter = {};
    UrlUtils.saveToLocalStorage(this.filterStorageKey, {}, true);
  }

  onSelectSortOption(event: FieldDef): void {
    this.selectedSortField = event.meta.sorting;
    this.filter = { ...this.filter };
    this.loadProjects();
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  /**
   * Event fired when the user clicks a deal circle
   * @param evt
   */
  onClickMapPoint(event: MapMouseEventExtended): void {
    this.selectedPoint = event.features[0];
  }

  /**
   * Event fired when the user clicks the analytics circle
   * @param evt
   */
  onClickAnalyticsPoint(event: MapMouseEventExtended): void {
    this.selectedAnalyticsPoint = event.features[0];
  }

  /**
   * From the speedial widget, specify the key to size circles by
   * @param val
   */
  onMapFilterChange(value: DealMapFilter): void {
    this.circleRadiusVar = value.key;
    this.selectedSpeadDial = value.id;
    this.isSpeadDialFixed = value.id ? true : !this.isSpeadDialFixed;
  }

  onMapLoaded(event: { target: Map; type: string }): void {
    this.map = event.target;
  }

  onMoveEnd(): void {
    this.mapTouched = true;
    this.loadProjects();
  }

  createProject(): void {
    this._matDialog.open(ProjectAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: this.data,
    });
  }

  /**
   * Change/toggle map view from deals and analytics.
   */
  onChangeMapView(event: MatButtonToggleChange): void {
    this.mapToggleVal = event.value;

    if (this.mapToggleVal == 'deals') {
      this.geoJsonUrl = `${this.baseApiUrl}/sharing/pipelines/${this.pipeline.id}/geojson`;
    } else {
      this.geoJsonUrl = `${this.baseApiUrl}/sharing/pipelines/${this.pipeline.id}/geojson?group_by=market`;
    }

    this._projectsService
      .getGeoJSON(this.geoJsonUrl)
      .pipe(untilComponentDestroyed(this))
      .subscribe((geoData: any) => {
        this.geoData = geoData;
        this._changeDetectorRef.markForCheck();
      });
  }

  onSpealDialClick(): void {
    this.isSpeadDialFixed = false;
  }

  getColor(index: number, length: number): string {
    if (length > 10) {
      let interval = 1 / length;
      return interpolateTurbo((index + 1) * interval);
    } else {
      return schemeCategory10[index];
    }
  }

  getIndex(marker: any): number {
    const field = this.fieldDefs.find((fieldDef) => fieldDef.name === this.groupBy);

    if (field.meta?.is_custom) {
      return field.meta.options.findIndex((option) => option === marker.properties[this.groupBy]);
    }

    return this.groupByOptions.findIndex((option) => option['id'] === marker.properties[this.groupBy]);
  }

  groupByChange(event: MatSelectChange): void {
    this.groupBy = event.value;

    const field = this.fieldDefs.find((fieldDef) => fieldDef.name === this.groupBy);

    if (field.meta?.is_custom) {
      this.groupByOptions = field.meta.options;
    } else {
      this.groupByOptions = this.data.options[field.meta.option_name];
    }

    if (this.groupByOptions === undefined) {
      this.groupByOptions = field.meta.options;
    }
  }

  groupName(group: any): string {
    if (typeof group === 'string') {
      return group;
    }

    return group?.name;
  }

  openCustomDrawer(): void {
    setupAddDealByDrawer(
      {
        projectService: this._projectsService,
        pipelineService: this.pipelineService,
        foldersTabService: this._foldersTabService,
        layoutService: this.layoutService,
        router: this._router,
        matDialog: this._matDialog,
        authService: this.authService,
      },
      {
        pipeline: this.pipeline,
        account: this.account,
        fieldDefs: this.fieldDefs,
        data: this.data,
        projectTemplates: this.projectTemplates,
        createPortfolio: true,
      },
    ).subscribe(noop);
  }

  togglePipelineMenu(): void {
    this.projectsListService.togglePipelineMenu();
  }

  onPageChange(value: 'next' | 'previous'): void {
    this.loadProjects(this.pagination.page + (value === 'next' ? 1 : -1));
  }

  canAddDeal(): boolean {
    return this.authService.hasPermission(Permission.DEALS_ADD);
  }
}
